import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';

import './RunningChart.css';
import { StravaActivity } from '../../types/strava';

interface RunningChartProps {
  activities: StravaActivity[];
  isCurrentYear: boolean;
  totalDaysInYear: number;
}

const RunningChart: React.FC<RunningChartProps> = ({ activities, isCurrentYear, totalDaysInYear }) => {
  const chartData = useMemo(() => {
    const dailyData = Array.from({ length: totalDaysInYear }, (_, index) => ({
      day: index,
      cumulativeDistance: 0,
    }));

    const sortedActivities = [...activities].sort(
      (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
    );

    let cumulativeDistance = 0;

    sortedActivities.forEach((activity) => {
      const activityDate = new Date(activity.start_date);
      const dayOfYear = Math.floor(
        (activityDate.getTime() - new Date(activityDate.getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24)
      );

      cumulativeDistance += Math.round(activity.distance / 1000);

      for (let i = dayOfYear; i < dailyData.length; i++) {
        dailyData[i].cumulativeDistance = cumulativeDistance;
      }
    });

    return dailyData;
  }, [activities, totalDaysInYear]);

  const today = Math.floor(
    (new Date().getTime() - new Date(new Date().getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24)
  );

  const rootStyles = getComputedStyle(document.documentElement);
  const accentStart = rootStyles.getPropertyValue('--color-accent-start').trim();
  const accentEnd = rootStyles.getPropertyValue('--color-accent-end').trim();

  const option = {
    xAxis: {
      type: 'value',
      min: 0,
      max: totalDaysInYear - 1,
      splitLine: {
        show: false,
      },
    },
    yAxis: {
      type: 'value',
      splitLine: {
        show: false,
      },
    },
    series: [
      {
        name: 'Cumulative Distance',
        type: 'line',
        silent: true,
        animation: false,
        data: chartData.map((item) => [item.day, item.cumulativeDistance]),
        lineStyle: {
          color: 'white',
          width: 1,
          opacity: 0.5,
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: accentEnd },
              { offset: 1, color: accentStart },
            ],
          },
        },
        symbolSize: 0,
      },
      {
        name: 'Target Line',
        type: 'line',
        silent: true,
        animation: false,
        data: [
          [0, 0],
          [totalDaysInYear, totalDaysInYear],
        ],
        lineStyle: {
          color: 'white',
          width: 1,
          opacity: 0.2,
        },
        symbolSize: 0,
        ...(isCurrentYear
          ? {
              markPoint: {
                data: [
                  {
                    coord: [today, today],
                    name: 'Today',
                  },
                ],
                silent: true,
                animation: false,
                symbol: 'circle',
                symbolSize: 5,
                itemStyle: {
                  color: 'white',
                  opacity: 0.5,
                },
              },
              markLine: {
                silent: true,
                animation: false,
                symbol: ['none', 'none'],
                data: [{ xAxis: today }],
                lineStyle: {
                  color: 'white',
                  width: 1,
                  opacity: 0.2,
                  type: 'line',
                },
              },
            }
          : {}),
      },
    ],
    grid: {
      left: -1,
      right: -1,
      top: -70,
      bottom: 0,
    },
  };

  return (
    <div className="running-chart">
      <ReactECharts option={option} style={{ height: '100%', width: '100%' }} opts={{ renderer: 'canvas' }} />
    </div>
  );
};

export default RunningChart;
